import {
  InfoOutlined,
  KeyOutlined,
  LockOutlined,
  SaveOutlined,
  SecurityOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { AdminDrawer, AdminSectionPanel, ErrorState, PageShell } from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import SsoSetupDetails from "@/pages/admin/integrations/sso/SsoSetupDetails";
import { paths } from "@/routes/paths";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import {
  SSO_PLATFORM_LABELS,
  SSO_PROTOCOL_LABELS,
  SsoPlatform,
  SsoProviderInput,
  SsoProviderService,
  SsoSetup,
} from "@/services/features/SsoProviderService";

const emptyProvider: SsoProviderInput = {
  name: "",
  protocol: "OIDC",
  issuerUri: "",
  samlMetadataUrl: "",
  clientId: "",
  clientSecret: "",
  tokenEndpointAuthMethod: "client_secret_basic",
  supportsWeb: true,
  supportsIos: false,
  supportsAndroid: false,
  redirectUri: "",
  enabled: false,
};

const PLATFORM_FLAGS: {
  platform: SsoPlatform;
  key: "supportsWeb" | "supportsIos" | "supportsAndroid";
}[] = [
  { platform: "WEB", key: "supportsWeb" },
  { platform: "IOS", key: "supportsIos" },
  { platform: "ANDROID", key: "supportsAndroid" },
];

type EditorState =
  | { status: "loading" | "error"; message?: string }
  | {
      status: "ready";
      customer: Customer;
      setup: SsoSetup;
      input: SsoProviderInput;
      secretConfigured: boolean;
    };

export default function SsoProviderEditor() {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);
  const { customerId: customerParam, providerId: providerParam } = useParams();
  const customerId = Number(customerParam);
  const providerId = providerParam === "new" ? null : Number(providerParam);
  const [state, setState] = useState<EditorState>({ status: "loading" });
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setState({ status: "loading" });
      try {
        if (
          !Number.isSafeInteger(customerId) ||
          customerId <= 0 ||
          (providerId !== null && (!Number.isSafeInteger(providerId) || providerId <= 0))
        ) {
          throw new Error("Invalid customer or provider");
        }
        const customer = await CustomerService.getInstance().getCustomerById(customerId);
        if (!customer.success || !customer.data) throw new Error("Customer not found");
        const setup = await SsoProviderService.getInstance().getSetup();
        let input = emptyProvider;
        let secretConfigured = false;
        if (providerId !== null) {
          const providers = await SsoProviderService.getInstance().getProviders(customerId);
          const provider = providers.find((entry) => entry.id === providerId);
          if (!provider) throw new Error("SSO provider not found for this customer");
          // The stored secret is never returned; an empty field means "keep it".
          input = {
            ...provider,
            clientSecret: "",
            redirectUri: provider.redirectUri ?? "",
            samlMetadataUrl: provider.samlMetadataUrl ?? "",
          };
          secretConfigured = provider.clientSecretConfigured;
        }
        if (active)
          setState({ status: "ready", customer: customer.data, setup, input, secretConfigured });
      } catch (error) {
        if (active)
          setState({
            status: "error",
            message: error instanceof Error ? error.message : "Unable to load provider",
          });
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [customerId, providerId, revision]);

  return (
    <PageShell
      breadcrumbs="admin"
      title={
        providerId === null
          ? t("management.sso.editor.addTitle")
          : state.status === "ready"
            ? t("management.sso.editor.editTitle", { customer: state.customer.name })
            : t("management.sso.editor.editFallback")
      }
      icon={<LockOutlined />}
      titleAccessory={<LoadingRing loading={state.status === "loading" || saving} />}
      headerActions={
        <Button
          variant="outlined"
          color="primary"
          startIcon={<InfoOutlined />}
          disabled={state.status !== "ready" || saving}
          onClick={() => setInfoOpen(true)}
        >
          {t("management.sso.editor.info")}
        </Button>
      }
    >
      {state.status === "error" && (
        <ErrorState
          title={state.message ?? "Unable to load provider"}
          onRetry={() => setRevision((value) => value + 1)}
        />
      )}
      <ProviderForm
        key={`${customerId}:${providerId}:${state.status === "ready" ? "ready" : "pending"}`}
        customer={state.status === "ready" ? state.customer : null}
        setup={state.status === "ready" ? state.setup : null}
        initialInput={state.status === "ready" ? state.input : emptyProvider}
        providerId={providerId}
        secretConfigured={state.status === "ready" && state.secretConfigured}
        loading={state.status !== "ready"}
        saving={saving}
        onSavingChange={setSaving}
      />
      <AdminDrawer
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={t("management.sso.editor.info")}
        icon={<InfoOutlined />}
        width={{ xs: "100%", md: "40%" }}
      >
        {state.status === "ready" && <SsoSetupDetails setup={state.setup} />}
      </AdminDrawer>
    </PageShell>
  );
}

function ProviderForm({
  customer,
  setup,
  initialInput,
  providerId,
  secretConfigured,
  loading,
  saving,
  onSavingChange,
}: {
  customer: Customer | null;
  setup: SsoSetup | null;
  initialInput: SsoProviderInput;
  providerId: number | null;
  secretConfigured: boolean;
  loading: boolean;
  saving: boolean;
  onSavingChange: (saving: boolean) => void;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState(initialInput);
  const [error, setError] = useState("");
  const busy = loading || saving;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const back = customer ? `${paths.admin.sso}?customerId=${customer.id}` : paths.admin.sso;
  const hasCallbacks = Object.values(setup?.redirectUris ?? {}).some(
    (list) => list && list.length > 0,
  );
  const saml = input.protocol === "SAML";
  const publicClient = saml || input.tokenEndpointAuthMethod === "none";

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !customer || !setup) return;
    setError("");
    if (!input.name.trim()) {
      setError("Enter a provider name.");
      return;
    }
    if (saml) {
      if (!input.issuerUri.trim() || /\s/.test(input.issuerUri.trim())) {
        setError("Enter the identity provider's entity ID exactly as shown in its metadata.");
        return;
      }
      try {
        const metadata = new URL(input.samlMetadataUrl.trim());
        if (metadata.protocol !== "https:" || metadata.username || metadata.password) {
          throw new Error();
        }
      } catch {
        setError("Enter the identity provider's federation metadata URL (HTTPS).");
        return;
      }
    } else {
      try {
        const issuer = new URL(input.issuerUri.trim());
        if (
          issuer.protocol !== "https:" ||
          issuer.username ||
          issuer.password ||
          issuer.search ||
          issuer.hash
        ) {
          throw new Error();
        }
      } catch {
        setError("Enter an absolute HTTPS issuer URL without credentials, a query, or a fragment.");
        return;
      }
      if (!input.clientId.trim()) {
        setError("Enter the client ID.");
        return;
      }
      if (!publicClient && !secretConfigured && !input.clientSecret.trim()) {
        setError("Enter the client secret issued by the customer's identity provider.");
        return;
      }
    }
    if (!input.supportsWeb && !input.supportsIos && !input.supportsAndroid) {
      setError("Select at least one platform this client serves.");
      return;
    }
    if (input.redirectUri.trim()) {
      try {
        const override = new URL(input.redirectUri.trim());
        if (override.username || override.password || override.search || override.hash)
          throw new Error();
      } catch {
        setError(
          "Enter the redirect URI exactly as the provider issued it, without a query or fragment.",
        );
        return;
      }
    }
    if (input.enabled && !input.redirectUri.trim() && !hasCallbacks) {
      setError("Configure the API callback URLs before enabling this provider.");
      return;
    }
    onSavingChange(true);
    try {
      await SsoProviderService.getInstance().saveProvider(customer.id, providerId, input);
      enqueueSnackbar("SSO provider saved", { variant: "success" });
      void navigate(back);
    } catch (error) {
      setError(error instanceof Error ? error.message : "Unable to save the provider");
    } finally {
      onSavingChange(false);
    }
  };

  return (
    <Box component="form" aria-busy={busy} onSubmit={(event) => void save(event)}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <AdminSectionPanel
          title={t("management.sso.editor.sections.provider")}
          icon={<SecurityOutlined />}
        >
          <Stack spacing={3}>
            <TextField
              label="Provider name"
              value={input.name}
              required
              fullWidth
              disabled={busy}
              helperText="Shown on the login page as “Continue with …”. Use the customer's organisation name, for example Acme Corp, rather than the identity provider vendor."
              slotProps={{ htmlInput: { maxLength: 100 } }}
              onChange={(event) => setInput({ ...input, name: event.target.value })}
            />
            <TextField
              select
              label="Protocol"
              required
              value={input.protocol}
              fullWidth
              disabled={busy}
              helperText="Whichever the customer's identity team registers Crave as. Both end in the same Crave login."
              onChange={(event) => {
                const value = event.target.value;
                if (value === "OIDC" || value === "SAML") setInput({ ...input, protocol: value });
              }}
            >
              <MenuItem value="OIDC">{SSO_PROTOCOL_LABELS.OIDC}</MenuItem>
              <MenuItem value="SAML">{SSO_PROTOCOL_LABELS.SAML}</MenuItem>
            </TextField>
            {saml ? (
              <>
                <TextField
                  label="Identity provider entity ID"
                  value={input.issuerUri}
                  required
                  fullWidth
                  disabled={busy}
                  helperText="The entityID in their federation metadata. In Microsoft Entra this is the “Microsoft Entra Identifier”, for example https://sts.windows.net/<tenant-id>/."
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                  onChange={(event) => setInput({ ...input, issuerUri: event.target.value })}
                />
                <TextField
                  label="Federation metadata URL"
                  type="url"
                  value={input.samlMetadataUrl}
                  required
                  fullWidth
                  disabled={busy}
                  helperText="Read on every login for the sign-in URL and signing certificates, so certificate rollover needs no change here. In Entra: “App Federation Metadata Url”."
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                  onChange={(event) => setInput({ ...input, samlMetadataUrl: event.target.value })}
                />
              </>
            ) : (
              <>
                <TextField
                  label="Issuer URL"
                  type="url"
                  value={input.issuerUri}
                  required
                  fullWidth
                  disabled={busy}
                  helperText="The exact issuer value from the provider's OpenID Connect discovery document."
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                  onChange={(event) => setInput({ ...input, issuerUri: event.target.value })}
                />
              </>
            )}
            <Box>
              <FormControlLabel
                label={t("management.sso.editor.fields.enableProvider")}
                sx={{ m: 0 }}
                control={
                  <Switch
                    checked={input.enabled}
                    disabled={
                      busy || (!hasCallbacks && !input.redirectUri.trim() && !input.enabled)
                    }
                    onChange={(_, enabled) => setInput({ ...input, enabled })}
                  />
                }
              />
              <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                Enable after the customer&apos;s application is configured. Saving changes
                invalidates pending SSO login attempts.
              </FormHelperText>
            </Box>
          </Stack>
        </AdminSectionPanel>
        <AdminSectionPanel
          title={t("management.sso.editor.sections.client")}
          icon={<KeyOutlined />}
        >
          <Stack spacing={3}>
            {!saml && (
              <TextField
                label="Client ID"
                value={input.clientId}
                required
                fullWidth
                disabled={busy}
                slotProps={{ htmlInput: { maxLength: 255 } }}
                onChange={(event) => setInput({ ...input, clientId: event.target.value })}
              />
            )}
            <FormControl component="fieldset" disabled={busy}>
              <FormLabel component="legend">Platforms</FormLabel>
              <FormGroup row>
                {PLATFORM_FLAGS.map(({ platform, key }) => (
                  <FormControlLabel
                    key={platform}
                    label={SSO_PLATFORM_LABELS[platform]}
                    control={
                      <Checkbox
                        checked={input[key]}
                        onChange={(_, checked) => setInput({ ...input, [key]: checked })}
                      />
                    }
                  />
                ))}
              </FormGroup>
              <FormHelperText>
                One row serves every platform the customer&apos;s application is registered for; the
                apps reach it through the API callbacks shown under customer setup requirements.
              </FormHelperText>
            </FormControl>
            {!saml && (
              <TextField
                select
                label="Token endpoint authentication"
                required
                value={input.tokenEndpointAuthMethod}
                fullWidth
                disabled={busy}
                helperText="Use the method registered for the customer's application. Public clients have no secret."
                onChange={(event) => {
                  const value = event.target.value;
                  if (
                    value === "client_secret_basic" ||
                    value === "client_secret_post" ||
                    value === "none"
                  ) {
                    setInput({ ...input, tokenEndpointAuthMethod: value });
                  }
                }}
              >
                <MenuItem value="client_secret_basic">Client secret in HTTP Basic header</MenuItem>
                <MenuItem value="client_secret_post">Client secret in request body</MenuItem>
                <MenuItem value="none">No client secret (public client)</MenuItem>
              </TextField>
            )}
            {!publicClient && (
              <TextField
                label="Client secret"
                type="password"
                value={input.clientSecret}
                required={!secretConfigured}
                fullWidth
                disabled={busy}
                autoComplete="new-password"
                helperText={
                  secretConfigured
                    ? "A secret is stored. Leave blank to keep it, or enter a new value to rotate it."
                    : "Issued by the customer's identity provider. Stored on the provider and never shown again."
                }
                slotProps={{ htmlInput: { maxLength: 1024 } }}
                onChange={(event) => setInput({ ...input, clientSecret: event.target.value })}
              />
            )}
            <TextField
              label="Redirect URI override"
              value={input.redirectUri}
              fullWidth
              disabled={busy}
              helperText="Leave blank to use the platform callbacks shown under customer setup requirements. Only for a provider that insists on a specific callback."
              slotProps={{ htmlInput: { maxLength: 500 } }}
              onChange={(event) => setInput({ ...input, redirectUri: event.target.value })}
            />
          </Stack>
        </AdminSectionPanel>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          startIcon={<SaveOutlined />}
          disabled={busy}
        >
          {t("management.sso.editor.actions.save")}
        </Button>
      </Box>
    </Box>
  );
}
